// src/hooks/useEvents.js - Hook replacement that uses EventsStore
import { useMemo } from 'react';
import useEventsStore from '../stores/EventsStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing events data from EventsStore
 * 
 * UPDATED: Now uses Zustand EventsStore instead of EventsProvider
 * Store initialization is handled at app level in SocketContext.
 * 
 * FIXED: Uses proper Zustand selectors instead of destructuring entire store
 * 
 * @returns {object} Events data and operations
 */
export const useEvents = () => {
  const socket = useSocket();
  
  // 🚨 FIXED: Use individual selectors instead of destructuring entire store
  
  // Main events data (single source of truth)
  const events = useEventsStore(state => state.events);
  const eventsLoading = useEventsStore(state => state.eventsLoading);
  const eventsPagination = useEventsStore(state => state.eventsPagination);

  // Calendar-specific data
  const calendarEvents = useEventsStore(state => state.calendarEvents);
  const calendarLoading = useEventsStore(state => state.calendarLoading);

  // Selected event data
  const selectedEvent = useEventsStore(state => state.selectedEvent);
  const selectedEventLoading = useEventsStore(state => state.selectedEventLoading);

  // Granular saving flags
  const creatingEvent = useEventsStore(state => state.creatingEvent);
  const savingEvent = useEventsStore(state => state.savingEvent);
  const deletingEvent = useEventsStore(state => state.deletingEvent);
  
  // Event operations (functions don't change, so we can get them once)
  const loadEvents = useEventsStore(state => state.loadEvents);
  const loadEventDetails = useEventsStore(state => state.loadEventDetails);
  const createEvent = useEventsStore(state => state.createEvent);
  const updateEvent = useEventsStore(state => state.updateEvent);
  const deleteEvent = useEventsStore(state => state.deleteEvent);
  const updateEventStatus = useEventsStore(state => state.updateEventStatus);
  
  // Utility functions
  const clearSelectedEvent = useEventsStore(state => state.clearSelectedEvent);
  const refreshEvents = useEventsStore(state => state.refreshEvents);
  const getEventById = useEventsStore(state => state.getEventById);
  const getCustomerEvents = useEventsStore(state => state.getCustomerEvents);
  const getEventsForDateRange = useEventsStore(state => state.getEventsForDateRange);
  const getAppointments = useEventsStore(state => state.getAppointments);
  
  // Connection state
  const isConnected = useEventsStore(state => state.isConnected);
  
  // Internal initialization state (for fallback check)
  const _initialized = useEventsStore(state => state._initialized);
  
  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useEvents: Store not initialized at app level, trying fallback');
    socket.initializeStore('EventsStore');
  }
  
  // Computed values (from EventsProvider)
  const appointments = useMemo(() => {
    return getAppointments();
  }, [getAppointments]);
  
  // Return the exact same interface as the old EventsProvider
  return {
    // Main events data (single source of truth)
    events,
    eventsLoading,
    eventsPagination,

    // Calendar-specific data
    calendarEvents,
    calendarLoading,

    // Selected event data
    selectedEvent,
    selectedEventLoading,

    // Granular saving flags
    creatingEvent,
    savingEvent,
    deletingEvent,

    // Computed values
    appointments,

    // Event operations
    loadEvents,
    loadEventDetails,
    createEvent,
    updateEvent,
    deleteEvent,
    updateEventStatus,

    // Utility functions
    clearSelectedEvent,
    refreshEvents,
    getEventById,
    getCustomerEvents,
    getEventsForDateRange,

    // Connection state
    isConnected: socket.isAuthenticated,

    // Legacy aliases for backward compatibility
    loading: eventsLoading,
    pagination: eventsPagination,
    selectedLoading: selectedEventLoading,
    calendar: calendarEvents,

    // Legacy error aliases (always undefined — errors handled by NotificationListener)
    eventsError: undefined,
    selectedEventError: undefined,
    clearEventsError: () => {},
  };
};

export default useEvents;